import { Injectable, Renderer2, RendererFactory2 } from '@angular/core';
import { Observable } from 'rxjs';
import { map } from 'rxjs/operators';
import { FileService } from './file.service';
import { IssueTrackerFileDTO } from '../models/issue-tracker-file-dto';

@Injectable({
  providedIn: 'root'
})
export class DynamicCssService {
  private renderer: Renderer2;
  private styleElements: Map<string, HTMLStyleElement> = new Map();
  private loadedCssUrls: Set<string> = new Set(); // Cache de URLs ya cargadas
  private loadedStyleIds: Set<string> = new Set(); // Cache de styleIds ya cargados
  private linkElements: Map<string, HTMLLinkElement> = new Map();

  constructor(
    private rendererFactory: RendererFactory2,
    private fileService: FileService
  ) {
    this.renderer = this.rendererFactory.createRenderer(null, null);
  }

  /**
   * Carga e inyecta CSS desde un archivo
   * @param cssFile Objeto IssueTrackerFileDTO con la información del archivo CSS
   * @param styleId ID único para identificar este estilo (opcional, se genera uno si no se proporciona)
   * @returns Observable que emite cuando el CSS ha sido cargado e inyectado
   */
  loadAndInjectCss(cssFile: IssueTrackerFileDTO, styleId?: string): Observable<void> {
    if (!cssFile || !cssFile.FileUri) {
      return new Observable(observer => {
        observer.next();
        observer.complete();
      });
    }

    const id = styleId || `dynamic-css-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;

    // Verificar si ya está cargado por URL o por styleId
    if (this.loadedCssUrls.has(cssFile.FileUri) || this.loadedStyleIds.has(id)) {
      return new Observable(observer => {
        observer.next();
        observer.complete();
      });
    }

    return this.fileService.getFileDataTextByUrl(cssFile.FileUri).pipe(
      map(cssContent => {
        this.injectCss(cssContent, id, cssFile.FileUri);
      })
    );
  }

  /**
   * Carga e inyecta CSS desde una URL directa
   * @param cssUrl URL del archivo CSS
   * @param styleId ID único para identificar este estilo (opcional)
   * @returns Observable que emite cuando el CSS ha sido cargado e inyectado
   */
  loadAndInjectCssFromUrl(cssUrl: string, styleId?: string): Observable<void> {
    if (!cssUrl) {
      return new Observable(observer => {
        observer.next();
        observer.complete();
      });
    }

    const id = styleId || `dynamic-css-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;

    // Verificar si ya está cargado por URL o por styleId
    if (this.loadedCssUrls.has(cssUrl) || this.loadedStyleIds.has(id)) {
      return new Observable(observer => {
        observer.next();
        observer.complete();
      });
    }

    return this.fileService.getFileDataTextByUrl(cssUrl).pipe(
      map(cssContent => {
        this.injectCss(cssContent, id, cssUrl);
      })
    );
  }

  /**
   * Inyecta CSS directamente en el DOM
   * @param cssContent Contenido CSS como string
   * @param styleId ID único para identificar este estilo
   * @param cssUrl URL del archivo CSS (opcional, para caché)
   */
  injectCss(cssContent: string, styleId: string, cssUrl?: string): void {
    if (!cssContent || !styleId) {
      return;
    }

    // Verificar si ya está cargado
    if (this.loadedStyleIds.has(styleId) || (cssUrl && this.loadedCssUrls.has(cssUrl))) {
      return;
    }

    // Verificar si el elemento ya existe en el DOM
    const existingElement = document.getElementById(styleId);
    if (existingElement) {
      this.styleElements.set(styleId, existingElement as HTMLStyleElement);
      this.loadedStyleIds.add(styleId);
      if (cssUrl) {
        this.loadedCssUrls.add(cssUrl);
      }
      return;
    }

    // Crear nuevo elemento style
    const styleElement = this.renderer.createElement('style');
    styleElement.type = 'text/css';
    styleElement.id = styleId;
    styleElement.textContent = cssContent;
    this.renderer.appendChild(document.head, styleElement);

    // Guardar referencia y marcar como cargado
    this.styleElements.set(styleId, styleElement);
    this.loadedStyleIds.add(styleId);
    if (cssUrl) {
      this.loadedCssUrls.add(cssUrl);
    }
  }

  /**
   * Verifica si un CSS ya está cargado
   * @param styleId ID del estilo a verificar
   * @returns true si el CSS ya está cargado
   */
  isCssLoaded(styleId: string): boolean {
    return this.loadedStyleIds.has(styleId) ||
           (document.getElementById(styleId) !== null);
  }

  /**
   * Elimina un estilo CSS específico del DOM
   * @param styleId ID del estilo a eliminar
   * @param removeFromCache Si es true, también lo elimina del caché (por defecto false para mantener en sesión)
   */
  removeCss(styleId: string, removeFromCache: boolean = false): void {
    const styleElement = this.styleElements.get(styleId) || document.getElementById(styleId);
    if (styleElement && document.head.contains(styleElement)) {
      this.renderer.removeChild(document.head, styleElement);
      this.styleElements.delete(styleId);

      if (removeFromCache) {
        this.loadedStyleIds.delete(styleId);
        // También eliminar de loadedCssUrls si es necesario
        // (esto requeriría mantener un mapeo inverso, por simplicidad lo dejamos así)
      }
    }
  }

  /**
   * Elimina todos los estilos CSS inyectados por este servicio
   */
  removeAllCss(): void {
    this.styleElements.forEach((element, id) => {
      if (document.head.contains(element)) {
        this.renderer.removeChild(document.head, element);
      }
    });
    this.styleElements.clear();
  }

  loadStylesheetLink(href: string, linkId: string): Promise<void> {
    return new Promise(resolve => {
      if (!href || !linkId) return resolve();

      const existing = document.getElementById(linkId) as HTMLLinkElement | null;
      if (existing) {
        // Si ya existe, asumimos cargado
        return resolve();
      }

      const linkEl = this.renderer.createElement('link') as HTMLLinkElement;
      linkEl.id = linkId;
      linkEl.rel = 'stylesheet';
      linkEl.href = href;
      linkEl.onload = () => resolve();
      linkEl.onerror = () => resolve(); // evita dejar el body oculto

      this.renderer.appendChild(document.head, linkEl);
    });
  }

  removeStylesheetLink(linkId: string, removeFromCache = false): void {
    const el = this.linkElements.get(linkId) || document.getElementById(linkId);
    if (el && document.head.contains(el)) {
      this.renderer.removeChild(document.head, el);
      this.linkElements.delete(linkId);
      if (removeFromCache) this.loadedStyleIds.delete(linkId);
    }
  }
}

